/**
 * What a supplier is paid per lead: `suppliers.settings.cost_per_lead` and
 * `cost_basis`, written to `leads.payout` when a lead arrives through the API
 * (see processing/supplier_cost.rs). A campaign can override the amount for
 * one supplier (`campaigns.settings.supplier_costs`); the basis stays the
 * supplier's.
 */
import { ActionIcon, Button, Group, NumberInput, Select, Stack, Text } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { formatGBP } from '../theme';
import { useVocab } from '../vocab';

export type CostBasis = 'per_lead' | 'per_sold';

export type SupplierCost = { amount: number | null; basis: CostBasis };

/** Read the cost out of a settings object. */
export function readSupplierCost(settings: unknown): SupplierCost {
  const s = (settings && typeof settings === 'object' ? settings : {}) as Record<string, unknown>;
  const raw = s.cost_per_lead;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
  return {
    amount: Number.isFinite(n) && n >= 0 ? n : null,
    basis: s.cost_basis === 'per_sold' ? 'per_sold' : 'per_lead',
  };
}

/** Put the cost into a settings object, keeping every other key. */
export function writeSupplierCost(settings: unknown, cost: SupplierCost): Record<string, unknown> {
  const s = { ...((settings && typeof settings === 'object' ? settings : {}) as Record<string, unknown>) };
  if (cost.amount == null) {
    delete s.cost_per_lead;
    delete s.cost_basis;
  } else {
    s.cost_per_lead = Math.round(cost.amount * 100) / 100;
    s.cost_basis = cost.basis;
  }
  return s;
}

/** "£2.50 per lead" or "£4.00 per sold lead", or null when none is set. */
export function useCostWords() {
  const v = useVocab();
  return (cost: SupplierCost): string | null =>
    cost.amount == null
      ? null
      : `${formatGBP(cost.amount)} per ${cost.basis === 'per_sold' ? `${v.p.sold} ${v.one('lead')}` : v.one('lead')}`;
}

export function SupplierCostFields({
  value,
  onChange,
  disabled,
}: {
  value: SupplierCost;
  onChange: (next: SupplierCost) => void;
  disabled?: boolean;
}) {
  const v = useVocab();
  return (
    <div>
      <Group grow align="flex-start">
        <NumberInput
          label={`Cost per ${v.one('lead')}`}
          description="What we pay them. Empty: no cost recorded."
          prefix="£"
          decimalScale={2}
          fixedDecimalScale
          min={0}
          disabled={disabled}
          value={value.amount ?? ''}
          onChange={(n) => onChange({ ...value, amount: typeof n === 'number' ? n : n === '' ? null : Number(n) })}
        />
        <Select
          label="Paid for"
          allowDeselect={false}
          disabled={disabled || value.amount == null}
          data={[
            { value: 'per_lead', label: `Every ${v.one('lead')} accepted` },
            { value: 'per_sold', label: `Only ${v.many('lead')} ${v.p.sold}` },
          ]}
          value={value.basis}
          onChange={(b) => b && onChange({ ...value, basis: b as CostBasis })}
        />
      </Group>
      <Text size="xs" c="dimmed" mt={6}>
        {`Every ${v.one('lead')} accepted counts each one that passes the ${v.one('campaign')}'s duplicate and suppression checks, sold or not. ${v.Many('lead')} from CSV imports are never charged, and a redistribute or return does not change what was charged.`}
      </Text>
    </div>
  );
}

export type CostOverride = { supplier_id: string; amount: number | '' };

/**
 * A campaign's per-supplier cost overrides (`settings.supplier_costs`). Each
 * row beats that supplier's own cost on this campaign only; the supplier's
 * basis (every lead, or sold ones) still applies.
 */
export function CampaignSupplierCosts({
  rows,
  onChange,
  suppliers,
}: {
  rows: CostOverride[];
  onChange: (next: CostOverride[]) => void;
  suppliers: { id: string; name: string; settings?: unknown }[];
}) {
  const v = useVocab();
  const costWords = useCostWords();
  const used = new Set(rows.map((r) => r.supplier_id));
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        {`What each ${v.one('supplier')} is paid per ${v.one('lead')} on this ${v.one('campaign')}, instead of their own cost. Whether they are paid for every ${v.one('lead')} or only ${v.many('lead')} ${v.p.sold} stays as set on the ${v.one('supplier')}.`}
      </Text>
      {rows.map((r, i) => {
        const sup = suppliers.find((s) => s.id === r.supplier_id);
        const own = sup ? costWords(readSupplierCost(sup.settings)) : null;
        return (
          <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
            <Select
              style={{ flex: 1 }}
              aria-label={v.One('supplier')}
              placeholder={`Choose a ${v.one('supplier')}`}
              searchable
              data={suppliers
                .filter((s) => s.id === r.supplier_id || !used.has(s.id))
                .map((s) => ({ value: s.id, label: s.name || s.id }))}
              value={r.supplier_id || null}
              onChange={(id) => onChange(rows.map((x, j) => (j === i ? { ...x, supplier_id: id ?? '' } : x)))}
              description={own ? `Their own cost: ${own}` : `No cost set on the ${v.one('supplier')}`}
            />
            <NumberInput
              w={140}
              aria-label="Cost"
              prefix="£"
              decimalScale={2}
          fixedDecimalScale
              min={0}
              value={r.amount}
              onChange={(n) => onChange(rows.map((x, j) => (j === i ? { ...x, amount: n === '' ? '' : Number(n) } : x)))}
            />
            <ActionIcon
              mt={6}
              variant="subtle"
              color="red"
              aria-label="Remove"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
            >
              <IconTrash size={15} />
            </ActionIcon>
          </Group>
        );
      })}
      <div>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={13} />}
          disabled={suppliers.every((s) => used.has(s.id))}
          onClick={() => onChange([...rows, { supplier_id: '', amount: '' }])}
        >
          {`Add a ${v.one('supplier')} cost`}
        </Button>
      </div>
    </Stack>
  );
}
